import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import humanTime from 'flarum/common/helpers/humanTime';
import Avatar from 'flarum/common/components/Avatar';
import extractText from 'flarum/common/utils/extractText';
import { typingTagIds } from '../typingCards';
import { fitIconsWhenReady } from '../util/fitIcons';

// Mithril global.

/**
 * A traditional forum-style category index (inspired by fof/categories): each
 * PARENT tag is a category header, with its CHILD tags listed as forum rows
 * (icon, name, description, discussion count, most-recent discussion). Flat
 * top-level tags (no children) are listed as forum rows under the forum title.
 * Shown in "category" layout mode on the home; click a forum to open it.
 */
export default class BespokeCategories extends Component {
  /*
   * A Kit logo glyph can be several times wider than the chip it sits in, and
   * nothing in CSS can measure that. fitIcons scales each glyph to its chip
   * after the DOM exists; it caches per glyph, so running it on every redraw
   * costs a lookup rather than a measurement.
   */
  oncreate(vnode) {
    super.oncreate(vnode);
    fitIconsWhenReady('.BespokeForum-icon', 0.86);
  }
  onupdate(vnode) {
    super.onupdate(vnode);
    fitIconsWhenReady('.BespokeForum-icon', 0.86);
  }

  // The index preloads only TOP-LEVEL tags, so childrenOf() finds nothing and
  // every category collapses into the flat "loners" block. Ask for the full
  // set once — the tags page does the same.
  static allTagsRequested = false;

  // Bug Reports carries 36 subforums. Showing them all made one card many times
  // taller than its neighbours (a grid row stretches to its tallest card), and
  // capping with a scrollbar just looked clipped. So the default shows a fixed
  // few and links to the rest.
  //
  // 🚨 It is a DEFAULT, not a rule: a board whose subforums are the point —
  // conferences listing their teams, say — wants all of them, and the tall-card
  // problem only bites when one card has far more children than its neighbours.
  // `layout.maxSubforums` decides, and 0 means show every one.
  static MAX_SUBS = 8;

  /**
   * A tag's logo, when something supplies one.
   *
   * 🚨 An <img> rather than a CSS background keyed off the tag's link. A
   * background cannot be told apart from "no logo" in CSS — there is no way to
   * branch on a custom property — so a theme could not put a plate behind the
   * crest only where a crest exists, and a conference whose brand colour IS
   * its logo colour (ACC navy on navy) rendered an invisible mark. An element
   * exists or it does not, which is the distinction the styling needs. It also
   * carries alt text, which a background never can.
   *
   * Reads an attribute nothing here provides: with no such extension installed
   * this is null and no crest is rendered.
   */
  static crest(tag, className) {
    let url = null;
    try {
      url = tag.attribute('logoUrl') || null;
    } catch (e) {
      return null;
    }
    if (!url) return null;
    return m('img', {
      className,
      src: url,
      alt: '',
      // Decorative: the forum's name is right beside it in both placements.
      'aria-hidden': 'true',
      loading: 'lazy'
    });
  }

  /**
   * How many unread discussions a reader has in a forum.
   *
   * 🚨 Server-side, not worked out from the recents sample on the page. That
   * sample is the newest hundred discussions on the whole board, so a forum
   * whose unread posts are older than that would show nothing new — which is
   * exactly the forum somebody most needs telling about.
   *
   * Absent for a guest and on an older Bespoke, and 0 reads as "nothing new",
   * so no marker is drawn in either case.
   */
  static unread(tag) {
    try {
      const n = tag.attribute('unreadCount');
      return typeof n === 'number' && n > 0 ? n : 0;
    } catch (e) {
      return 0;
    }
  }

  /** Set once the store's payload hook is in place; the patch is global. */
  static storeHooked = false;

  /**
   * Redraw whenever anything is pushed into the store.
   *
   * 🚨 Debounced, and only ever installed once. `pushPayload` runs for every
   * API response on the page — redrawing synchronously inside it would turn a
   * paginated discussion list into one redraw per page of results.
   */
  static subscribeToStore() {
    if (BespokeCategories.storeHooked) return;
    const store = app.store;
    if (!store || typeof store.pushPayload !== 'function') return;
    BespokeCategories.storeHooked = true;
    const original = store.pushPayload.bind(store);
    let queued = null;
    store.pushPayload = payload => {
      const result = original(payload);
      if (!queued) {
        queued = setTimeout(() => {
          queued = null;
          m.redraw();
        }, 80);
      }
      return result;
    };
  }

  /** How many subforum chips this site shows per card; 0 = all of them. */
  static maxSubs() {
    let n;
    try {
      n = app.bespoke?.document?.tokens?.layout?.maxSubforums ?? app.forum.attribute('bespoke.published')?.tokens?.layout?.maxSubforums;
    } catch (e) {
      n = undefined;
    }

    // Absent means an older document that predates the setting, which must keep
    // the behaviour it has. Only an explicit 0 means "all".
    if (typeof n !== 'number' || n < 0) return 8;
    return n;
  }

  // How many recent discussions each forum card lists.
  static RECENT_PER_FORUM = 2;

  // tagId -> most recent discussions, built from ONE request rather than a
  // query per forum.
  static recentByTag = {};

  /** Handle for the store watcher, cleared on unmount. */
  storeWatch = null;
  onremove() {
    if (this.storeWatch) clearTimeout(this.storeWatch);
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.watchStore();
    if (BespokeCategories.allTagsRequested) return;
    BespokeCategories.allTagsRequested = true;
    try {
      // Include the latest discussion (and its author) or every forum row
      // renders "No discussions yet" — the tag carries only a relationship
      // link, and the discussion itself is not otherwise in the store.
      app.store.find('tags', {
        include: 'lastPostedDiscussion,lastPostedDiscussion.lastPostedUser'
      }).then(() => m.redraw()).catch(() => {});
      // The newest discussions across the whole forum, grouped by tag below —
      // a per-forum query would be one request per card, and a board with a
      // hundred and fifty forums would open with a hundred and fifty requests.
      this.loadRecent();
    } catch (e) {
      /* store not ready */
    }
  }

  /**
   * Redraw when the tag store grows, whoever filled it.
   *
   * 🚨 Watched rather than timed. The request below is guarded by a STATIC
   * flag so a second mount does not re-fetch — but the early return took its
   * redraw with it, and the index fills the tag store on its own regardless.
   * The cards then rendered before the children existed and never rendered
   * again: the store held all hundred and thirty-five subforums while the page
   * showed one per card, for as long as the page stayed open.
   *
   * Fixed timeouts were the first attempt and were wrong for the honest
   * reason — they fire when they fire, and on a board of a hundred and fifty
   * tags the data arrived after the last of them. A component cannot subscribe
   * to the store, so this watches the one number that matters and stops as
   * soon as it settles.
   */
  watchStore() {
    /*
     * 🚨 Subscribe first, and treat the poll below as a backstop only.
     *
     * The poll stopped as soon as the tag count held steady for three ticks —
     * about a second — and the store does not fill in one burst. On a board of
     * a hundred and fifty tags the index's own payload arrives, the count sits
     * still long enough to look settled, the watcher quits, and the rest land
     * afterwards to no redraw. A conference with sixteen teams then showed the
     * five it happened to have when the music stopped, permanently, and it
     * looked exactly like a cap.
     *
     * `pushPayload` is where every one of them arrives, whoever asked for it,
     * so wrapping it once is a real subscription rather than a guess about
     * timing. Patched at most once per page, and it only schedules a redraw.
     */
    BespokeCategories.subscribeToStore();
    let last = -1;
    let settled = 0;
    const started = Date.now();
    const tick = () => {
      let n = 0;
      try {
        n = (app.store.all('tags') || []).length;
      } catch (e) {
        n = 0;
      }
      if (n !== last) {
        last = n;
        settled = 0;
        m.redraw();
      } else {
        settled++;
      }

      // Stop once the count has held steady for a couple of checks, or after
      // thirty seconds — whichever comes first. A watcher that never stops is
      // a timer left running on every page a reader visits.
      if (settled < 3 && Date.now() - started < 30000) {
        this.storeWatch = setTimeout(tick, 400);
      }
    };
    tick();
  }

  /**
   * The newest discussions on the board, in at most two requests.
   *
   * 🚨 The sample size decides how many forums get a recent block, and 50 is
   * the API's own ceiling on this list — asking for 60 got 50 and read as if
   * it had asked for 60. On a board with more forums than that sample reaches,
   * the older ones fell back to the tag's single last-posted pointer and showed
   * one row where their neighbours showed two, which looks like a bug rather
   * than a limit.
   *
   * So: page again, once, when the first page comes back full. Two requests
   * covers a hundred discussions and stops — this is a card footer, not a
   * search, and a board with a thousand forums must not open with twenty
   * requests to fill in the last few.
   */
  loadRecent(page = 0, carry = []) {
    const LIMIT = 50;
    const MAX_PAGES = 2;
    app.store.find('discussions', {
      sort: '-lastPostedAt',
      page: {
        limit: LIMIT,
        offset: page * LIMIT
      },
      include: 'tags,lastPostedUser'
    }).then(list => {
      const all = carry.concat(list || []);
      const byTag = {};
      all.forEach(d => {
        let tags = [];
        try {
          tags = d.tags() || [];
        } catch (e) {
          tags = [];
        }
        tags.filter(Boolean).forEach(t => {
          const id = String(t.id());
          (byTag[id] = byTag[id] || []).push(d);
        });
      });
      BespokeCategories.recentByTag = byTag;
      m.redraw();

      // A short page is the last page; there is nothing to ask for.
      if ((list || []).length === LIMIT && page + 1 < MAX_PAGES) {
        this.loadRecent(page + 1, all);
      }
    }).catch(() => {});
  }

  /**
   * Newest discussions in a forum AND everything under it.
   *
   * 🚨 The whole subtree, not the card's own tag.
   *
   * A parent that holds subforums usually holds no discussions itself — that
   * is what makes it a parent. fbsfb.com is the clearest case there is: every
   * conference card read zero and said "No discussions yet" while forty-nine
   * team forums beneath them held the lot, the SEC alone carrying twenty-seven.
   * A category card that reports only its own tag is answering a question
   * nobody asked it.
   */
  recentFor(tag, children = []) {
    const seen = new Set();
    const out = [];
    for (const t of [tag, ...children]) {
      for (const d of BespokeCategories.recentByTag[String(t.id())] || []) {
        const id = String(d.id());
        if (!seen.has(id)) {
          seen.add(id);
          out.push(d);
        }
      }
    }
    if (out.length) {
      return out.sort((a, b) => BespokeCategories.postedAt(b) - BespokeCategories.postedAt(a)).slice(0, BespokeCategories.RECENT_PER_FORUM);
    }

    /*
     * Fall back to the tags' own pointers while the discussion fetch is in
     * flight, or when the newest 60 discussions contain nothing from this
     * subtree — which on a big board is most cards most of the time.
     */
    const pointers = [tag, ...children].map(t => t.lastPostedDiscussion && t.lastPostedDiscussion()).filter(Boolean);
    return pointers.sort((a, b) => BespokeCategories.postedAt(b) - BespokeCategories.postedAt(a)).slice(0, BespokeCategories.RECENT_PER_FORUM);
  }

  /** When a discussion was last posted in, as a number that sorts. */
  static postedAt(discussion) {
    try {
      const at = discussion.lastPostedAt && discussion.lastPostedAt();
      return at ? new Date(at).getTime() : 0;
    } catch (e) {
      return 0;
    }
  }

  /**
   * A card's totals, counting everything beneath it.
   *
   * 🚨 NOT a sum. Flarum gives a discussion in a child tag its parent tag too,
   * so the parent's own count already includes its children's discussions;
   * adding them again counted every sub-tag discussion twice (Bug Reports read
   * 29 for 17). The largest single count is the subtree's total: the parent's,
   * or a child's when older data tagged the child without its parent.
   *
   * Worked out on the CLIENT from counts already in the store (the chips are
   * drawn from the same child tags), rather than a second API field.
   */
  static totals(tag, children) {
    let discussions = 0;
    let posts = null;
    for (const t of [tag, ...children]) {
      try {
        discussions = Math.max(discussions, t.discussionCount && t.discussionCount() || 0);
      } catch (e) {
        /* a tag mid-load has no count yet */
      }
      try {
        // postCount is a Bespoke-added API attribute (no generated model
        // accessor), so it is read via attribute(); null when absent (Tags
        // disabled, an older server). Null stays null: a total of 0 would
        // claim there are no posts rather than that nobody counted.
        const p = t.attribute ? t.attribute('postCount') : null;
        if (typeof p === 'number') posts = Math.max(posts ?? 0, p);
      } catch (e) {
        /* ditto */
      }
    }
    return {
      discussions,
      posts
    };
  }
  view() {
    const all = app.store.all('tags') || [];
    // Cheap: a Set built once per redraw, not once per card.
    const typing = typingTagIds();
    // Top level = has no parent. Filtering on isChild() is unreliable here:
    // it is computed from `position` and `parent`, and on the index those are
    // only populated once the full tag set has loaded.
    const topLevel = all.filter(t => t && t.position && t.position() !== null && !(t.parent && t.parent())).sort((a, b) => (a.position() || 0) - (b.position() || 0));
    if (!topLevel.length) return null;

    // Every top-level tag is a forum card; its children are listed INSIDE that
    // card as subforum chips, the way an IPS forum row carried its subforums.
    // (Previously a parent became a category header and its children became
    // separate cards, which split one forum into several.)
    return m("div", {
      className: "BespokeCategories"
    }, m("div", {
      className: "BespokeCat"
    }, m("div", {
      className: "BespokeCat-forums"
    }, topLevel.map(f => this.forum(f, this.childrenOf(f, all), typing)))));
  }

  /**
   * A forum's subforums, from the store rather than from the relationship.
   *
   * 🚨 The store WINS, and that is the whole of this fix. `tag.children()`
   * returns whatever the API happened to include, which on the index is a
   * partial list — and the old code took it whenever it was truthy. An empty
   * array is truthy in JavaScript, so `children() || all.filter(...)` never
   * once reached its fallback: a card whose relationship came back with one
   * subforum showed one, a card whose came back empty showed none, and no
   * later redraw could mend it because the relationship never grows.
   *
   * Live on a board of sixteen conferences that meant sixteen chips where
   * there should have been a hundred and thirty-five, and it looked like a
   * cap rather than a fault.
   *
   * The component asks for the full tag set on init, so once that lands the
   * store is the complete answer. The relationship is kept only for the
   * moment before it arrives.
   */
  childrenOf(tag, all) {
    /*
     * 🚨 The parent's ID out of the raw relationship, not `c.parent()`.
     *
     * `parent()` has to resolve the related MODEL, which only works once that
     * tag is in the store and linked — and on the index it is not, at the
     * moment the first render runs. So the filter matched nothing, every card
     * fell through to its partial relationship, and no later redraw fixed it
     * because by then the render had already been committed.
     *
     * The id is in the payload the moment the tag arrives, so this is true on
     * the first pass.
     */
    const id = String(tag.id());
    const fromStore = all.filter(c => {
      try {
        const rel = c.data?.relationships?.parent?.data;
        return rel && String(rel.id) === id;
      } catch (e) {
        return false;
      }
    });
    const kids = fromStore.length ? fromStore : tag.children && tag.children() || [];
    return kids.slice().sort((a, b) => (a.position() || 0) - (b.position() || 0));
  }
  category(title, color, desc, forums) {
    return m("div", {
      className: "BespokeCat",
      style: color ? `--cat: ${color}` : undefined
    }, m("div", {
      className: "BespokeCat-header"
    }, m("span", {
      className: "BespokeCat-title"
    }, title), desc ? m("span", {
      className: "BespokeCat-desc"
    }, desc) : null), m("div", {
      className: "BespokeCat-forums"
    }, forums.map(f => this.forum(f))));
  }
  forum(tag, children = [], typing = new Set()) {
    const cap = BespokeCategories.maxSubs();
    const color = tag.color && tag.color();
    const recent = this.recentFor(tag, children);
    const tagHref = app.route.tag ? app.route.tag(tag) : app.route('index');
    // The card covers everything beneath it — see totals().
    const {
      discussions,
      posts
    } = BespokeCategories.totals(tag, children);

    // NB: a plain <a> does a FULL page reload in Flarum (resets the SPA history),
    // which is what made the discussion back-button fall through to home() — and
    // the row must NOT be an <a> wrapping another <a> (invalid). So the row is a
    // div with proper SPA Links (oncreate: m.route.Link) for the forum + last post.
    return m("div", {
      className: "BespokeForum",
      style: color ? `--cat: ${color}` : undefined
      // Opting this card in to live presence. ernestdefoe/presence fills any
      // element carrying this attribute with a "N here" bar and keeps it
      // current; the card only has to say which forum it is, and shows
      // nothing at all when that extension is not installed.
      ,
      "data-presence-tag": tag.id()
    }, BespokeCategories.crest(tag, 'BespokeForum-crest'), m("a", {
      className: "BespokeForum-link",
      href: tagHref,
      oncreate: m.route.Link
    }, m("span", {
      className: "BespokeForum-icon"
    }, tag.icon && tag.icon() ? m('i', {
      className: tag.icon()
    }) : (tag.name() || '#').charAt(0)), m("div", {
      className: "BespokeForum-main"
    }, m("span", {
      className: "BespokeForum-name"
    }, tag.name(), BespokeCategories.unread(tag) ? m('span.BespokeForum-new', {
      title: extractText(app.translator.trans('ernestdefoe-bespoke.forum.unread_title'))
    }, app.translator.trans('ernestdefoe-bespoke.forum.unread_count', {
      count: BespokeCategories.unread(tag)
    })) : null, typing.has(String(tag.id())) ? m('span.Bespoke-tagTyping', {
      title: extractText(app.translator.trans('ernestdefoe-bespoke.forum.tag_typing'))
    }, [m('i'), m('i'), m('i')]) : null), tag.description && tag.description() ? m("span", {
      className: "BespokeForum-desc"
    }, tag.description()) : null)), children.length ? m("div", {
      className: "BespokeForum-subs"
    }, (cap ? children.slice(0, cap) : children).map(c => m('a.BespokeForum-sub', {
      href: app.route.tag ? app.route.tag(c) : app.route('index'),
      oncreate: m.route.Link,
      style: c.color && c.color() ? `--sub: ${c.color()}` : undefined
    }, [BespokeCategories.crest(c, 'BespokeForum-subCrest'), c.icon && c.icon() ? m('i', {
      className: c.icon()
    }) : null, m('span', c.name()),
    // A chip is too small for a count; a dot is all it can carry
    // without becoming a second line of text.
    BespokeCategories.unread(c) ? m('span.BespokeForum-subNew') : null])), cap && children.length > cap ? m('a.BespokeForum-sub.BespokeForum-sub--more', {
      href: tagHref,
      oncreate: m.route.Link
    }, '+' + (children.length - cap) + ' more') : null) : null, m("div", {
      className: "BespokeForum-stats"
    }, m("span", {
      className: "BespokeForum-stat"
    }, m("b", null, discussions), " ", discussions === 1 ? 'discussion' : 'discussions'), posts !== null ? m("span", {
      className: "BespokeForum-stat"
    }, m("b", null, posts), " ", posts === 1 ? 'post' : 'posts') : null), m("div", {
      className: "BespokeForum-last"
    }, recent.length ? recent.map(d => {
      const who = d.lastPostedUser && d.lastPostedUser() || d.user && d.user();
      return m('.BespokeForum-lastRow', [who ? m('span.BespokeForum-lastAvatar', m(Avatar, {
        user: who
      })) : null, m('.BespokeForum-lastBody', [m('a.BespokeForum-lastTitle', {
        href: app.route.discussion(d),
        oncreate: m.route.Link
      }, d.title()), m('span.BespokeForum-lastMeta', [who ? m('span.BespokeForum-lastWho', who.displayName()) : null, who && d.lastPostedAt && d.lastPostedAt() ? ' · ' : null, d.lastPostedAt && d.lastPostedAt() ? humanTime(d.lastPostedAt()) : null])])]);
    }) : m('span.BespokeForum-lastMeta', app.translator.trans('ernestdefoe-bespoke.forum.categories_no_discussions'))));
  }
}
flarum.reg.add('ernestdefoe-bespoke', 'forum/components/BespokeCategories', BespokeCategories);